/**
 * The utilities in this file support derived data dealing with `ClauseValue.text_search` interfaces (which are temporarily represented and expected to be improved in the future).
 */
import type { IntlShape } from 'react-intl';

import { Option } from '../types/Option';
import {
  ClauseOperatorId,
  TextSearch,
  TextSearchContains,
  TextSearchScope,
} from '../types/Value';

export const getTextSearchPairLookup = (
  clauseOperatorId: ClauseOperatorId,
  intl: IntlShape,
) => {
  const containsLabel = intl.formatMessage({
    id: 'getTextSearchPairLookup.contains',
    defaultMessage: 'contains',
    description: 'Text search contains operator label',
  });

  const exactPhraseLabel = intl.formatMessage({
    id: 'getTextSearchPairLookup.exactPhrase',
    defaultMessage: 'exact phrase',
    description: 'Text search scope for exact phrase',
  });

  const doesNotContainLabel = intl.formatMessage({
    id: 'getTextSearchPairLookup.doesNotContain',
    defaultMessage: 'does not contain',
    description: 'Text search does not contain operator label',
  });

  const allOfTheseWordsLabel = intl.formatMessage({
    id: 'getTextSearchPairLookup.allOfTheseWords',

    defaultMessage: 'all of these words',
    description: 'Text search scope for all words',
  });

  const anyOfTheseWordsLabel = intl.formatMessage({
    id: 'getTextSearchPairLookup.anyOfTheseWords',
    defaultMessage: 'any of these words',
    description: 'Text search scope for any words',
  });

  const pair: Record<ClauseOperatorId, [TextSearchContains, TextSearchScope]> =
    {
      contains_all_of_these_words: [
        {
          value: 'contains',
          label: containsLabel,
        },
        {
          value: 'all_of_these_words',
          label: allOfTheseWordsLabel,
        },
      ],
      contains_any_of_these_words: [
        {
          value: 'contains',
          label: containsLabel,
        },
        {
          value: 'any_of_these_words',
          label: anyOfTheseWordsLabel,
        },
      ],
      contains_exact_phrase: [
        {
          value: 'contains',
          label: containsLabel,
        },
        {
          value: 'exact_phrase',
          label: exactPhraseLabel,
        },
      ],
      does_not_contain_all_of_these_words: [
        {
          value: 'does_not_contain',
          label: doesNotContainLabel,
        },
        {
          value: 'all_of_these_words',
          label: allOfTheseWordsLabel,
        },
      ],
      does_not_contain_any_of_these_words: [
        {
          value: 'does_not_contain',
          label: doesNotContainLabel,
        },
        {
          value: 'any_of_these_words',
          label: anyOfTheseWordsLabel,
        },
      ],
      does_not_contain_exact_phrase: [
        {
          value: 'does_not_contain',
          label: doesNotContainLabel,
        },
        {
          value: 'exact_phrase',
          label: exactPhraseLabel,
        },
      ],
    };
  return pair[clauseOperatorId];
};

export const getTextSearchOptionsLookup = (string: string, intl: IntlShape) => {
  const searchOptions: Record<
    string,
    Record<string, Option<ClauseOperatorId>>
  > = {
    contains: {
      all_of_these_words: {
        value: 'contains_all_of_these_words',
        label: intl.formatMessage({
          id: 'getTextSearchOptionsLookup.containsAllOfTheseWords',
          defaultMessage: 'contains all of these words',
          description: 'Text search option for contains all words',
        }),
      },
      any_of_these_words: {
        value: 'contains_any_of_these_words',
        label: intl.formatMessage({
          id: 'getTextSearchOptionsLookup.containsAnyOfTheseWords',
          defaultMessage: 'contains any of these words',
          description: 'Text search option for contains any words',
        }),
      },
      exact_phrase: {
        value: 'contains_exact_phrase',
        label: intl.formatMessage({
          id: 'getTextSearchOptionsLookup.containsExactPhrase',
          defaultMessage: 'contains exact phrase',
          description: 'Text search option for contains exact phrase',
        }),
      },
    },
    does_not_contain: {
      all_of_these_words: {
        value: 'does_not_contain_all_of_these_words',
        label: intl.formatMessage({
          id: 'getTextSearchOptionsLookup.doesNotContainAllOfTheseWords',
          defaultMessage: 'does not contain all of these words',
          description: 'Text search option for does not contain all words',
        }),
      },
      any_of_these_words: {
        value: 'does_not_contain_any_of_these_words',
        label: intl.formatMessage({
          id: 'getTextSearchOptionsLookup.doesNotContainAnyOfTheseWords',
          defaultMessage: 'does not contain any of these words',
          description: 'Text search option for does not contain any words',
        }),
      },
      exact_phrase: {
        value: 'does_not_contain_exact_phrase',
        label: intl.formatMessage({
          id: 'getTextSearchOptionsLookup.doesNotContainExactPhrase',
          defaultMessage: 'does not contain exact phrase',
          description: 'Text search option for does not contain exact phrase',
        }),
      },
    },
  };
  return searchOptions[string];
};

export const toTextSearchOption = (
  textSearch: TextSearch,
  intl: IntlShape,
): Option<ClauseOperatorId> => {
  const { contains, scope } = textSearch;
  return getTextSearchOptionsLookup(contains.value, intl)[scope.value];
};

export const toTextSearchPair = (
  ClauseOperatorId: ClauseOperatorId,
  intl: IntlShape,
): [TextSearchContains, TextSearchScope] => {
  return getTextSearchPairLookup(ClauseOperatorId, intl);
};
